import { useEffect, useState } from "react";
import { useCompositionStore } from "../../stores/compositionStore.js";
import * as api from "../../infra/api.js";
import { Button } from "../../ui/Button.js";
import { Card } from "../../ui/Card.js";

/**
 * Exports the song's live stage as a downloadable MP4 clip (Plan 13 §8.3).
 * Mirrors `ExportPanel`: the button enqueues a `clip-render` job via the
 * dedicated `POST /songs/:id/clip` route, progress streams through SSE
 * (`job.percent`), and on `ClipRendered` the content-addressed `.mp4` becomes
 * downloadable through the assets route.
 */
export function ClipPanel(): JSX.Element {
  const projectId = useCompositionStore((s) => s.projectId);
  const jobs = useCompositionStore((s) => s.jobs);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Recover the tracked job after a refresh: the local `jobId` useState is lost
  // on reload, but the SSE replay rebuilds `jobs` from the log — the last
  // `clip-render` job in the store is the one this panel was following.
  useEffect(() => {
    if (jobId !== null) return;
    const clipJobs = Object.values(jobs).filter((j) => j.kind === "clip-render");
    if (clipJobs.length > 0) setJobId(clipJobs[clipJobs.length - 1]!.id);
  }, [jobs, jobId]);

  const job = jobId ? (jobs[jobId] ?? null) : null;
  const isRunning = job?.status === "running" || job?.status === "pending";
  const downloadHash =
    job?.status === "completed" && job.outputHash !== undefined ? job.outputHash : null;
  const downloadUrl =
    downloadHash !== null && projectId !== null ? api.assetUrl(projectId, downloadHash) : null;

  async function onRenderClip(): Promise<void> {
    const pid = useCompositionStore.getState().projectId;
    if (!pid) return;
    setError(null);
    try {
      const { jobId: id } = await api.renderClip(pid);
      // Register the job as pending so SSE progress updates land on it.
      useCompositionStore.setState((s) => ({
        jobs: { ...s.jobs, [id]: { id, kind: "clip-render", status: "pending" } },
      }));
      setJobId(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const buttonLabel = isRunning ? `Renderizando clipe ${job?.percent ?? 0}%` : "Exportar clipe";

  return (
    <Card aria-label="Export clip">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              void onRenderClip();
            }}
            disabled={!projectId || isRunning}
          >
            {buttonLabel}
          </Button>
          {downloadUrl !== null ? (
            <a
              className="font-mono text-[11px] uppercase tracking-widest2 text-butter underline"
              href={downloadUrl}
              download
              aria-label="Download clip"
            >
              Baixar MP4
            </a>
          ) : null}
        </div>
        {error !== null ? (
          <span role="alert" className="text-xs text-accent-danger">
            {error}
          </span>
        ) : null}
      </div>
    </Card>
  );
}
